import React, { MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/pro-solid-svg-icons";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { ButtonNew } from "components/buttonNew";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";

import "./note.scss";

type TNoteBase = {
  authorName: string;
  authorImg?: any;
  content: string;
  dateString: string;
  limitWidth?: boolean;
  onRemove?: (e: MouseEvent<SVGSVGElement, globalThis.MouseEvent>) => void;
  highPriority?: boolean;
};

type TNote = TNoteBase & {
  quickNote?: false;
  authorEmail?: never;
  onEdit?: (e: MouseEvent<HTMLDivElement>) => void;
  title?: string;
};

type TQuickNoteProps = TNoteBase & {
  quickNote?: true;
  authorEmail?: string;
  onEdit?: never;
  title?: never;
};

// quickNote prop determines different optional props of the component
export type TNoteProps = TNote | TQuickNoteProps;

export default function Note(props: TNoteProps) {
  const { t } = useTranslation();
  const [removeModal, setRemoveModal] = useState({
    open: false,
    event: null as MouseEvent<SVGSVGElement, globalThis.MouseEvent>,
  });

  useEffect(() => {
    if (removeModal.open) {
      setRemoveModal({ open: false, event: null });
    }
  }, [props.onRemove]);

  const { quickNote = false } = props;

  function removeNote() {
    props.onRemove(removeModal.event);
    setRemoveModal({ open: false, event: null });
  }

  return (
    <>
      {!quickNote ? (
        <div className="ui-note">
          <div className="ui-note-content">
            <div className="ui-note-top">
              {props.title && <p className="title">{props.title}</p>}
              <p className="date">{props.dateString}</p>
            </div>
            <p className="ui-note-description">{props.content}</p>
            <div className="ui-note-bottom">
              <div className="ui-note-images">
                {props.highPriority ? <FontAwesomeIcon icon={faCircleExclamation} size="lg" /> : null}
                <FontAwesomeIcon icon={["far", "user-alt"]} size="lg" />
              </div>
              &nbsp;<p>{props.authorName}</p>
            </div>
          </div>
          <div className="ui-note-action">
            {props.onEdit && (
              <ButtonNew type="text" onClick={props.onEdit}>
                {t("Edit")}
              </ButtonNew>
            )}
            <span style={{ minWidth: "28px" }}>
              {props.onRemove && (
                <FontAwesomeIcon icon={faX} color="grey" onClick={e => setRemoveModal({ open: true, event: e })} />
              )}
            </span>
          </div>
        </div>
      ) : (
        <>
          {props.highPriority ? (
            <div className={"ui-quick-note-warning"}>
              <div className="ui-note-images">
                <div className="ui-note-img-error">
                  <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                </div>
              </div>
              <div className="ui-note-description">
                <p className={props.limitWidth ? "ui-note-description-limit_width" : "ui-note-description-text"}>
                  {props.content}
                </p>
                <div>
                  {props?.authorName ? (
                      <p className={props.limitWidth ? "ui-note-description-limit_width" : "ui-note-description-text"}>
                        <span className="warning-tint">{t('Posted by')}</span> {props.authorName}
                      </p>
                  ) : null}
                  {props.authorEmail && <p className="email">{props.authorEmail}</p>}
                </div>
              </div>
            </div>
          ) : (
            <div className={"ui-quick-note"}>
              <div className="ui-note-images">
                {props.highPriority ? (
                  <div className="ui-note-img-error">
                    <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                  </div>
                ) : null}
                <div className="ui-note-img">
                  <FontAwesomeIcon icon={["far", "user-alt"]} size="lg" />
                </div>
              </div>
              <div className="ui-note-description">
                <p className={props.limitWidth ? "ui-note-description-limit_width" : "ui-note-description"}>
                  {props.authorName} <span className="tint">{t('posted')}</span> {props.content}
                </p>
                {props.authorEmail && <p className="email">{props.authorEmail}</p>}
              </div>

              <div className="ui-note-action">
                {props.onRemove && (
                  <FontAwesomeIcon icon={faX} color="grey" onClick={e => setRemoveModal({ open: true, event: e })} />
                )}
              </div>
            </div>
          )}
        </>
      )}

      <Portal isMounted={removeModal.open}>
        {/* DELETE NOTE POPUP */}
        <Popup
          type="warning"
          open={removeModal.open}
          title={t("Delete Note")}
          description={t("You are about to permanently remove this note. Are you sure?")}
          closable
          onCancel={() => setRemoveModal({ open: false, event: null })}
          onOk={() => removeNote()}
          cancelText={t("Cancel")}
          okText={t("Delete")}
        />
      </Portal>
    </>
  );
}
